import {
  AbnStatusEnum,
  CreateWorkspaceDto,
  ErrorResponse,
  GstStatusEnum,
  MembershipStatusEnum,
  PaginatedWorkspacesDto,
  UpdateWorkspaceBusinessDetailsDto,
  UpdateWorkspaceContactDetailsDto,
  UpdateWorkspaceDetailsDto,
  UpdateWorkspaceDto,
  Workspace,
  WorkspaceStatusEnum,
} from '@sr-sdks/api-sdk-axios';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  GetWorkspacesApi,
  workspaceService,
} from '../../reducers/workspaces/workspaceSlice';
import { useAppDispatch } from '../../stateStore';
import {
  bypassCacheConfig,
  invalidateCacheRequestConfig,
} from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

export const WORKSPACES_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 25,
  sortBy: 'workspaceName:asc',
} as const satisfies WorkspacesPaginatedParams;

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const workspacesQueryKeys = {
  all: ['workspaces'] as const,
  lists: () => [...workspacesQueryKeys.all, 'list'] as const,
  paginated: (params?: WorkspacesPaginatedParams) =>
    [...workspacesQueryKeys.all, 'paginated', { ...params }] as const,
  single: (workspaceId?: string) =>
    [...workspacesQueryKeys.all, 'single', { workspaceId }] as const,
};

// ===== API FUNCTIONS =====

export interface WorkspacesPaginatedParams {
  abnStatus?: AbnStatusEnum[];
  allowPayments?: boolean;
  gstStatus?: GstStatusEnum[];
  membershipStatus?: MembershipStatusEnum[];
  name?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  workspaceStatus?: WorkspaceStatusEnum[];
}

export interface UpdateWorkspaceDetailsParams {
  id: string;
  updateWorkspaceDetailsDto: UpdateWorkspaceDetailsDto;
}

export interface UpdateWorkspaceContactDetailsParams {
  id: string;
  updateWorkspaceContactDetailsDto: UpdateWorkspaceContactDetailsDto;
}

export interface UpdateWorkspaceBusinessDetailsParams {
  id: string;
  updateWorkspaceBusinessDetailsDto: UpdateWorkspaceBusinessDetailsDto;
}

export interface UpdateWorkspaceParams {
  id: string;
  updateWorkspaceDto: UpdateWorkspaceDto;
}

export interface CreateWorkspaceParams {
  createWorkspaceDetailsDto: CreateWorkspaceDto;
}

/**
 * Fetch all workspaces
 */
export const fetchWorkspaces = async (): Promise<Workspace[]> => {
  try {
    const response = await GetWorkspacesApi().workspaceControllerFindAll();

    return response.data as Workspace[];
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'workspaceControllerFindAll',
    });
  }
};

/**
 * Fetch workspaces with pagination
 */
export const fetchWorkspacesPaginated = async (
  params: WorkspacesPaginatedParams,
) => {
  try {
    const response = await GetWorkspacesApi().workspaceControllerFindAllV2(
      params?.abnStatus,
      params?.allowPayments,
      params?.gstStatus,
      params?.membershipStatus,
      params?.name,
      undefined,
      params?.workspaceStatus,
      params?.page || 1,
      params?.pageSize || 10,
      params?.sortBy || 'workspaceName:asc',
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'workspaceControllerFindAllV2',
      params: params,
    });
  }
};

/**
 * Fetch a single workspace by ID
 */
export const fetchWorkspaceById = async (id: string): Promise<Workspace> => {
  try {
    // React Query is the only cache for this read; the Redux thunks that read
    // the same URLs keep the axios cache, so the invalidations below stay.
    const response = await GetWorkspacesApi().workspaceControllerFindOne(
      id,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'workspaceControllerFindOne',
      params: { id },
    });
  }
};

/**
 * Update workspace details (including membership status)
 */
export const updateWorkspaceDetails = async ({
  id,
  updateWorkspaceDetailsDto,
}: UpdateWorkspaceDetailsParams): Promise<Workspace> => {
  try {
    const response = await GetWorkspacesApi().workspaceControllerUpdateDetails(
      id,
      updateWorkspaceDetailsDto,
      {
        ...invalidateCacheRequestConfig('workspace'),
        // Every caller of this wrapper toasts the rejection, so the global
        // "error on our end" modal would only contradict the message they
        // show — including the Entity Details lock's 403 (APP-2254). The
        // legacy forms go through the Redux thunk, which has no toast, so
        // they keep the modal.
        suppressErrorModal: true,
      },
    );

    return response.data;
  } catch (error) {
    // `toApiError` like the reads above: WorkspaceDetailsForm gates its toast on
    // `error instanceof ApiError`, so a bare body loses the server's message —
    // including the lock's 403 explaining the refusal (APP-2254).
    throw toApiError(error, {
      endpoint: 'workspaceControllerUpdateDetails',
      params: { id },
    });
  }
};

/**
 * Update workspace contact details
 */
export const updateWorkspaceContactDetails = async ({
  id,
  updateWorkspaceContactDetailsDto,
}: UpdateWorkspaceContactDetailsParams): Promise<Workspace> => {
  try {
    const response =
      await GetWorkspacesApi().workspaceControllerUpdateContactDetails(
        id,
        updateWorkspaceContactDetailsDto,
        invalidateCacheRequestConfig(`workspace/${id}`),
      );

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    throw (
      (axiosError.response?.data as ErrorResponse) ||
      new Error('Failed to update workspace contact details')
    );
  }
};

/**
 * Update workspace business details
 */
export const updateWorkspaceBusinessDetails = async ({
  id,
  updateWorkspaceBusinessDetailsDto,
}: UpdateWorkspaceBusinessDetailsParams): Promise<Workspace> => {
  try {
    const response =
      await GetWorkspacesApi().workspaceControllerUpdateBusinessDetails(
        id,
        updateWorkspaceBusinessDetailsDto,
        invalidateCacheRequestConfig(`workspace/${id}`),
      );

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    throw (
      (axiosError.response?.data as ErrorResponse) ||
      new Error('Failed to update workspace business details')
    );
  }
};

/**
 * Update workspace
 */
export const updateWorkspace = async ({
  id,
  updateWorkspaceDto,
}: UpdateWorkspaceParams): Promise<Workspace> => {
  try {
    const response = await GetWorkspacesApi().workspaceControllerUpdate(
      id,
      updateWorkspaceDto,
      invalidateCacheRequestConfig('workspace'),
    );

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    throw (
      (axiosError.response?.data as ErrorResponse) ||
      new Error('Failed to update workspace')
    );
  }
};

/**
 * Create workspace
 */
export const createWorkspace = async ({
  createWorkspaceDetailsDto,
}: CreateWorkspaceParams): Promise<Workspace> => {
  try {
    const response = await GetWorkspacesApi().workspaceControllerCreate(
      createWorkspaceDetailsDto,
    );

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    throw (
      (axiosError.response?.data as ErrorResponse) ||
      new Error('Failed to create workspace')
    );
  }
};

// ===== QUERY OPTIONS =====

/**
 * This function is defined outside of the query options
 * to give it a stable reference. Thus, preventing
 * unnecessary re-renders from a hook.
 *
 * It transforms the returned data to only
 * include workspaces with an ID.
 */
const selectWorkspacesPaginated = (
  data: PaginatedWorkspacesDto,
): PaginatedWorkspacesDto => {
  return {
    ...data,
    items: data.items.filter((v) => Boolean(v._id)),
  };
};

export const queryOptionsGetWorkspacesPaginated = (
  params?: WorkspacesPaginatedParams,
) => {
  params = {
    ...WORKSPACES_DEFAULT_PARAMS,
    ...params,
  };

  return queryOptions({
    placeholderData: keepPreviousData,
    queryFn: params ? () => fetchWorkspacesPaginated(params) : skipToken,
    queryKey: workspacesQueryKeys.paginated(params),
    select: selectWorkspacesPaginated,
  });
};

export const queryOptionsGetWorkspace = (workspaceId?: string) => {
  return queryOptions({
    placeholderData: keepPreviousData,
    queryFn: workspaceId ? () => fetchWorkspaceById(workspaceId) : skipToken,
    queryKey: workspacesQueryKeys.single(workspaceId),
  });
};

// ===== MUTATION HOOKS =====

/**
 * Hook for updating workspace details (including membership status)
 */
export const useUpdateWorkspaceDetails = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: updateWorkspaceDetails,
    onSuccess: (data, variables) => {
      // Update the specific workspace in cache
      queryClient.setQueryData(workspacesQueryKeys.single(variables.id), data);

      // Settings pages read workspace from Redux; sync so UI (e.g. entity name lock) matches membership status
      dispatch(workspaceService.actions.applyWorkspaceUpdate(data));

      // Invalidate lists to ensure they're refetched
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKeys.lists(),
      });

      // Invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKeys.paginated(),
      });
    },
  });
};

/**
 * Hook for updating workspace contact details
 */
export const useUpdateWorkspaceContactDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateWorkspaceContactDetails,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(workspacesQueryKeys.single(variables.id), data);
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKeys.lists(),
      });
    },
  });
};

/**
 * Hook for updating workspace business details
 */
export const useUpdateWorkspaceBusinessDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateWorkspaceBusinessDetails,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(workspacesQueryKeys.single(variables.id), data);
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKeys.lists(),
      });
    },
  });
};

/**
 * Hook for updating workspace
 */
export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateWorkspace,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(workspacesQueryKeys.single(variables.id), data);
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKeys.lists(),
      });
    },
  });
};

/**
 * Hook for creating workspace
 */
export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKeys.paginated(),
      });
    },
  });
};
